// ═══════════════════════════════════════════════════════════════
// V·IANet Governance — V·IA Personas: Directorio Corporativo
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback, useRef } from 'react';
import { useI18n } from '../../../../../i18n';
import { AppHeader } from '../../../../shared/components/AppHeader';
import {
  fetchPersonas,
  fetchPersonaEstadisticas,
  fetchDepartamentos,
  crearPersona,
  type PersonaResumen,
  type PersonaEstadisticas,
} from '../../../../shared/services/api';
import { PersonaDrawer } from '../components/PersonaDrawer';
import { C } from '../../../../../lib/design';
import { InfoTooltip } from '@/components/ui/InfoTooltip';

// ─── Constants (labels resolved via i18n in component) ───
const TIPO_PERSONA_KEYS = [
  { value: '', labelKey: 'allTypes' as const },
  { value: 'EMPLEADO', labelKey: 'employee' as const },
  { value: 'COLABORADOR', labelKey: 'collaborator' as const },
  { value: 'EXTERNO', labelKey: 'external' as const },
];

const ESTADO_KEYS = [
  { value: '', labelKey: 'all' as const },
  { value: 'ACTIVO', labelKey: 'activeFilter' as const },
  { value: 'BAJA', labelKey: 'inactiveFilter' as const },
];

const TIPO_BADGE_COLORS: Record<string, { bg: string; text: string }> = {
  EMPLEADO: { bg: '#EFF6FF', text: '#1D4ED8' },
  COLABORADOR: { bg: '#FFF7ED', text: '#C2410C' },
  EXTERNO: { bg: '#F5F3FF', text: '#7C3AED' },
};

const ESTADO_BADGE_COLORS: Record<string, { bg: string; text: string }> = {
  ACTIVO: { bg: '#F0FDF4', text: '#16A34A' },
  BAJA: { bg: '#FEF2F2', text: '#DC2626' },
};

// ─── Helpers ───
function getInitials(nombre: string, apellidos: string): string {
  return `${nombre.charAt(0)}${apellidos.charAt(0)}`.toUpperCase();
}

// ═══════════════════════════════════════════════════════════════
// STATS CARDS
// ═══════════════════════════════════════════════════════════════
function StatsCards({
  stats,
  filtered,
}: {
  stats: PersonaEstadisticas | null;
  filtered?: boolean;
}) {
  const { t } = useI18n();
  if (!stats) return null;

  const cards = [
    { label: t.people.total, value: stats.totalPersonas, color: 'var(--color-primario)' },
    { label: t.people.employees, value: stats.empleados, color: '#1D4ED8' },
    { label: t.people.collaborators, value: stats.colaboradores, color: '#C2410C' },
    { label: t.people.externals, value: stats.externos, color: '#7C3AED' },
    { label: t.people.active, value: stats.activos, color: '#16A34A' },
    { label: t.people.inactive, value: stats.bajas, color: '#DC2626' },
    { label: t.people.noPosition, value: stats.sinPuesto, color: C.gray400 },
  ];

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: filtered ? 4 : 20 }}>
        {cards.map((c) => (
          <div
            key={c.label}
            style={{
              flex: '1 1 120px',
              minWidth: 110,
              background: '#fff',
              border: '1px solid #E2E6ED',
              borderRadius: 8,
              padding: '12px 16px',
              textAlign: 'center',
            }}
          >
            <div style={{ fontSize: 24, fontWeight: 700, color: c.color }}>{c.value}</div>
            <div style={{ fontSize: 11, color: '#5A6171', marginTop: 2 }}>{c.label}</div>
          </div>
        ))}
      </div>
      {filtered && (
        <div style={{ fontSize: 11, color: C.gray400, marginBottom: 16, fontStyle: 'italic' }}>
          {t.people.filteredStats}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// CREATE PERSONA MODAL
// ═══════════════════════════════════════════════════════════════
// ─── Validation helpers ───
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface FieldErrors {
  codigo?: string;
  nombre?: string;
  apellidos?: string;
  email?: string;
}

function validatePersonaForm(
  form: { codigo: string; nombre: string; apellidos: string; email: string },
  v: { required: string; emailFormat: string },
): FieldErrors {
  const errors: FieldErrors = {};
  if (!form.codigo.trim()) errors.codigo = v.required;
  if (!form.nombre.trim()) errors.nombre = v.required;
  if (!form.apellidos.trim()) errors.apellidos = v.required;
  if (!form.email.trim()) errors.email = v.required;
  else if (!EMAIL_REGEX.test(form.email)) errors.email = v.emailFormat;
  return errors;
}

function FieldError({ msg }: { msg?: string }) {
  if (!msg) return null;
  return <div style={{ fontSize: 11, color: '#DC2626', marginTop: 2 }}>{msg}</div>;
}

export function CreatePersonaModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useI18n();
  const [form, setForm] = useState({
    codigo: '',
    nombre: '',
    apellidos: '',
    email: '',
    telefono: '',
    tipoPersona: 'EMPLEADO' as 'EMPLEADO' | 'COLABORADOR' | 'EXTERNO',
    fechaAlta: new Date().toISOString().slice(0, 10),
    tipoContrato: '',
    codigoDireccion: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const handleChange = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setError('');
    // Re-validate touched fields
    if (touched[field]) {
      setFieldErrors((prev) => {
        const updated = { ...prev };
        const newForm = { ...form, [field]: value };
        const errs = validatePersonaForm(newForm, t.people.validation);
        if (errs[field as keyof FieldErrors]) {
          updated[field as keyof FieldErrors] = errs[field as keyof FieldErrors];
        } else {
          delete updated[field as keyof FieldErrors];
        }
        return updated;
      });
    }
  };

  const handleBlur = (field: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const errs = validatePersonaForm(form, t.people.validation);
    if (errs[field as keyof FieldErrors]) {
      setFieldErrors((prev) => ({
        ...prev,
        [field as keyof FieldErrors]: errs[field as keyof FieldErrors],
      }));
    } else {
      setFieldErrors((prev) => {
        const updated = { ...prev };
        delete updated[field as keyof FieldErrors];
        return updated;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validatePersonaForm(form, t.people.validation);
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      setTouched({ codigo: true, nombre: true, apellidos: true, email: true });
      return;
    }
    setSaving(true);
    try {
      await crearPersona({
        ...form,
        telefono: form.telefono || undefined,
        tipoContrato: form.tipoContrato || undefined,
        codigoDireccion: form.codigoDireccion || undefined,
      });
      onCreated();
      onClose();
      setForm({
        codigo: '',
        nombre: '',
        apellidos: '',
        email: '',
        telefono: '',
        tipoPersona: 'EMPLEADO',
        fechaAlta: new Date().toISOString().slice(0, 10),
        tipoContrato: '',
        codigoDireccion: '',
      });
      setFieldErrors({});
      setTouched({});
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t.people.errorCreatePerson);
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const hasErrors = Object.keys(fieldErrors).length > 0;

  const inputBase: React.CSSProperties = {
    width: '100%',
    padding: '8px 14px',
    border: '1px solid #D1D5DB',
    borderRadius: 8,
    fontSize: 14,
    fontFamily: 'inherit',
    outline: 'none',
  };

  const inputErr: React.CSSProperties = { ...inputBase, borderColor: '#DC2626' };

  const labelSt: React.CSSProperties = {
    display: 'block',
    fontSize: 12,
    fontWeight: 600,
    color: C.gray700,
    marginBottom: 4,
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.4)',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 12,
          padding: 24,
          width: 480,
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ margin: '0 0 16px', fontSize: 18, color: 'var(--color-primario)' }}>
          {t.people.newPersonTitle}
        </h2>

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelSt}>{t.people.form.code}</label>
              <input
                style={fieldErrors.codigo ? inputErr : inputBase}
                value={form.codigo}
                onChange={(e) => handleChange('codigo', e.target.value)}
                onBlur={() => handleBlur('codigo')}
                placeholder={t.people.placeholders.code}
              />
              <FieldError msg={fieldErrors.codigo} />
            </div>
            <div>
              <label style={labelSt}>{t.people.form.personType}</label>
              <select
                style={inputBase}
                value={form.tipoPersona}
                onChange={(e) => handleChange('tipoPersona', e.target.value)}
              >
                <option value="EMPLEADO">{t.people.employee}</option>
                <option value="COLABORADOR">{t.people.collaborator}</option>
                <option value="EXTERNO">{t.people.external}</option>
              </select>
            </div>
            <div>
              <label style={labelSt}>{t.people.form.firstName}</label>
              <input
                style={fieldErrors.nombre ? inputErr : inputBase}
                value={form.nombre}
                onChange={(e) => handleChange('nombre', e.target.value)}
                onBlur={() => handleBlur('nombre')}
              />
              <FieldError msg={fieldErrors.nombre} />
            </div>
            <div>
              <label style={labelSt}>{t.people.form.lastName}</label>
              <input
                style={fieldErrors.apellidos ? inputErr : inputBase}
                value={form.apellidos}
                onChange={(e) => handleChange('apellidos', e.target.value)}
                onBlur={() => handleBlur('apellidos')}
              />
              <FieldError msg={fieldErrors.apellidos} />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={labelSt}>{t.people.form.email}</label>
              <input
                type="email"
                style={fieldErrors.email ? inputErr : inputBase}
                value={form.email}
                onChange={(e) => handleChange('email', e.target.value)}
                onBlur={() => handleBlur('email')}
              />
              <FieldError msg={fieldErrors.email} />
            </div>
            <div>
              <label style={labelSt}>{t.people.form.phone}</label>
              <input
                style={inputBase}
                value={form.telefono}
                onChange={(e) => handleChange('telefono', e.target.value)}
              />
            </div>
            <div>
              <label style={labelSt}>{t.people.form.hireDate}</label>
              <input
                type="date"
                style={inputBase}
                value={form.fechaAlta}
                onChange={(e) => handleChange('fechaAlta', e.target.value)}
              />
            </div>
            <div>
              <label style={labelSt}>{t.people.form.contractType}</label>
              <input
                style={inputBase}
                value={form.tipoContrato}
                onChange={(e) => handleChange('tipoContrato', e.target.value)}
                placeholder={t.people.placeholders.contractType}
              />
            </div>
            <div>
              <label style={labelSt}>{t.people.form.directionCode}</label>
              <input
                style={inputBase}
                value={form.codigoDireccion}
                onChange={(e) => handleChange('codigoDireccion', e.target.value)}
              />
            </div>
          </div>

          {error && (
            <div
              style={{
                marginTop: 12,
                padding: '8px 12px',
                background: '#FEF2F2',
                color: '#DC2626',
                borderRadius: 6,
                fontSize: 12,
              }}
            >
              {error}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '8px 16px',
                border: '1px solid #D1D5DB',
                borderRadius: 6,
                background: '#fff',
                cursor: 'pointer',
                fontSize: 13,
                fontFamily: 'inherit',
              }}
            >
              {t.common.cancelar}
            </button>
            <button
              type="submit"
              disabled={saving || hasErrors}
              style={{
                padding: '8px 16px',
                border: 'none',
                borderRadius: 6,
                background: saving || hasErrors ? C.gray400 : 'var(--color-secundario)',
                color: '#fff',
                cursor: saving || hasErrors ? 'not-allowed' : 'pointer',
                fontSize: 13,
                fontWeight: 600,
                fontFamily: 'inherit',
              }}
            >
              {saving ? t.people.creatingPerson : t.people.createPerson}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// PERSONA CARD
// ═══════════════════════════════════════════════════════════════
function getDeptColor(deptName: string | undefined): string {
  if (!deptName) return '#1F3864';
  const DEPT_COLORS = [
    '#1F3864',
    '#2F5496',
    '#33BCE1',
    '#1A94BF',
    '#4472C4',
    '#16507D',
    '#2E75B6',
    '#375F8C',
  ];
  let hash = 0;
  for (let i = 0; i < deptName.length; i++) hash = deptName.charCodeAt(i) + ((hash << 5) - hash);
  return DEPT_COLORS[Math.abs(hash) % DEPT_COLORS.length];
}

function PersonaCard({ persona, onClick }: { persona: PersonaResumen; onClick?: () => void }) {
  const initials = getInitials(persona.nombre, persona.apellidos);
  const tipoBadge = TIPO_BADGE_COLORS[persona.tipoPersona] ?? { bg: '#F3F4F6', text: C.gray500 };
  const estadoBadge = ESTADO_BADGE_COLORS[persona.estado] ?? { bg: '#F3F4F6', text: C.gray500 };
  const puesto = persona.puestoActual?.puesto;
  const avatarBg = persona.fotoUrl
    ? `url(${persona.fotoUrl}) center/cover`
    : getDeptColor(puesto?.departamento?.nombre);

  return (
    <div
      onClick={onClick}
      style={{
        background: '#fff',
        border: '1px solid #E2E6ED',
        borderRadius: 10,
        padding: 16,
        cursor: onClick ? 'pointer' : 'default',
        display: 'flex',
        gap: 12,
        alignItems: 'flex-start',
        transition: 'box-shadow 0.15s',
      }}
      onMouseEnter={(e) => {
        if (onClick) e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = '';
      }}
    >
      {/* Avatar */}
      <div
        style={{
          width: 48,
          height: 48,
          borderRadius: 12,
          background: avatarBg,
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 16,
          fontWeight: 700,
          flexShrink: 0,
        }}
      >
        {!persona.fotoUrl && initials}
      </div>

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
          <span style={{ fontWeight: 600, fontSize: 16, color: '#1A1D23' }}>
            {persona.nombre} {persona.apellidos}
          </span>
        </div>

        <div style={{ fontSize: 14, color: '#5A6171', marginBottom: 4 }}>{persona.email}</div>

        {puesto && (
          <div style={{ fontSize: 14, color: C.gray700, marginBottom: 6 }}>
            {puesto.titulo}
            {puesto.departamento && (
              <span style={{ color: C.gray400 }}> / {puesto.departamento.nombre}</span>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: 6 }}>
          <span
            style={{
              display: 'inline-block',
              padding: '4px 12px',
              borderRadius: 4,
              fontSize: 13,
              fontWeight: 500,
              background: tipoBadge.bg,
              color: tipoBadge.text,
            }}
          >
            {persona.tipoPersona}
          </span>
          <span
            style={{
              display: 'inline-block',
              padding: '4px 12px',
              borderRadius: 4,
              fontSize: 13,
              fontWeight: 500,
              background: estadoBadge.bg,
              color: estadoBadge.text,
            }}
          >
            {persona.estado}
          </span>
          <span style={{ fontSize: 10, color: C.gray400, padding: '2px 4px' }}>
            {persona.codigo}
          </span>
          {(persona.asignacionesActivasCount ?? 0) > 1 && (
            <span
              style={{
                display: 'inline-block',
                padding: '2px 8px',
                borderRadius: 12,
                fontSize: 11,
                fontWeight: 600,
                background: '#FFF7ED',
                color: '#C2410C',
              }}
            >
              {persona.asignacionesActivasCount} puestos
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════
export function PersonasPage({ embedded = false }: { embedded?: boolean }) {
  const { t } = useI18n();
  const [personas, setPersonas] = useState<PersonaResumen[]>([]);
  const [stats, setStats] = useState<PersonaEstadisticas | null>(null);
  const [departamentos, setDepartamentos] = useState<Array<{ id: string; nombre: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Filters
  const [search, setSearch] = useState('');
  const [tipoPersona, setTipoPersona] = useState('');
  const [estado, setEstado] = useState('');
  const [departamentoId, setDepartamentoId] = useState('');

  // View mode (grid/list)
  const [viewMode, setViewMode] = useState<'grid' | 'list'>(
    () => (localStorage.getItem('vianet-personas-view') as 'grid' | 'list') || 'grid',
  );
  const toggleView = (mode: 'grid' | 'list') => {
    setViewMode(mode);
    localStorage.setItem('vianet-personas-view', mode);
  };

  // Drawer
  const [drawerPersonaId, setDrawerPersonaId] = useState<string | null>(null);

  // Selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === personas.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(personas.map((p) => p.id)));
    }
  };

  // Debounce search (must be before useEffects that reference debouncedSearch)
  const searchTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [search]);

  // Clear selection on page/filter change
  useEffect(() => {
    setSelectedIds(new Set());
  }, [page, pageSize, debouncedSearch, tipoPersona, estado, departamentoId]);

  const handleExportCsv = () => {
    const selected = personas.filter((p) => selectedIds.has(p.id));
    const headers = [
      'Codigo',
      'Nombre',
      'Apellidos',
      'Email',
      'Telefono',
      'Tipo',
      'Estado',
      'Puesto',
      'Departamento',
    ];
    const rows = selected.map((p) => [
      p.codigo,
      p.nombre,
      p.apellidos,
      p.email ?? '',
      p.telefono ?? '',
      p.tipoPersona,
      p.estado,
      p.puestoActual?.puesto?.titulo ?? '',
      p.puestoActual?.puesto?.departamento?.nombre ?? '',
    ]);
    const csv = [headers, ...rows]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `personas_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Load data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = { page: String(page), pageSize: String(pageSize) };
      if (debouncedSearch) params.q = debouncedSearch;
      if (tipoPersona) params.tipoPersona = tipoPersona;
      if (estado) params.estado = estado;
      if (departamentoId) params.departamento = departamentoId;

      const res = await fetchPersonas(params);
      setPersonas(res.data);
      setTotalPages(res.meta.totalPages);
      setTotalItems(res.meta.total);
    } catch (err) {
      console.error('Error loading personas:', err);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, debouncedSearch, tipoPersona, estado, departamentoId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Load departments once
  useEffect(() => {
    fetchDepartamentos()
      .then((r) => setDepartamentos(r.data))
      .catch(() => {});
  }, []);

  // Load stats reactively when filters change
  const hasActiveFilters = !!(debouncedSearch || tipoPersona || estado || departamentoId);

  const buildStatsParams = useCallback((): Record<string, string> | undefined => {
    const params: Record<string, string> = {};
    if (debouncedSearch) params.q = debouncedSearch;
    if (tipoPersona) params.tipoPersona = tipoPersona;
    if (estado) params.estado = estado;
    if (departamentoId) params.departamento = departamentoId;
    return Object.keys(params).length > 0 ? params : undefined;
  }, [debouncedSearch, tipoPersona, estado, departamentoId]);

  const reloadStats = useCallback(() => {
    fetchPersonaEstadisticas(buildStatsParams())
      .then((r) => setStats(r.data))
      .catch(() => {});
  }, [buildStatsParams]);

  useEffect(() => {
    reloadStats();
  }, [reloadStats]);

  const selectStyle: React.CSSProperties = {
    padding: '8px 14px',
    border: '1px solid #D1D5DB',
    borderRadius: 8,
    fontSize: 14,
    fontFamily: 'inherit',
    background: '#fff',
    outline: 'none',
    color: C.gray700,
    minWidth: 160,
  };

  return (
    <div style={{ background: 'var(--wl-background, #ECEEF2)', minHeight: '100vh' }}>
      {!embedded && <AppHeader modulo="Structure" titulo="Directorio de Personas" />}

      <div style={{ padding: '20px 32px', maxWidth: 1200, margin: '0 auto' }}>
        {!embedded && (
          <h1
            style={{
              fontSize: 18,
              fontWeight: 700,
              color: '#1F3864',
              margin: '0 0 16px 0',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            {t.structure.personasTitle}
            <InfoTooltip
              title={t.structure.tooltips.personas.titulo}
              text={t.structure.tooltips.personas.ayuda}
            />
          </h1>
        )}

        {/* Stats */}
        <StatsCards stats={stats} filtered={hasActiveFilters} />

        {/* Filters */}
        <div
          style={{
            display: 'flex',
            gap: 10,
            marginBottom: 16,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <input
            type="text"
            placeholder={t.people.searchPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              flex: '1 1 250px',
              padding: '8px 14px',
              border: '1px solid #D1D5DB',
              borderRadius: 8,
              fontSize: 14,
              fontFamily: 'inherit',
              outline: 'none',
            }}
          />
          <select
            style={selectStyle}
            value={tipoPersona}
            onChange={(e) => {
              setTipoPersona(e.target.value);
              setPage(1);
            }}
          >
            {TIPO_PERSONA_KEYS.map((o) => (
              <option key={o.value} value={o.value}>
                {t.people[o.labelKey]}
              </option>
            ))}
          </select>
          <select
            style={selectStyle}
            value={estado}
            onChange={(e) => {
              setEstado(e.target.value);
              setPage(1);
            }}
          >
            {ESTADO_KEYS.map((o) => (
              <option key={o.value} value={o.value}>
                {t.people[o.labelKey]}
              </option>
            ))}
          </select>
          <select
            style={selectStyle}
            value={departamentoId}
            onChange={(e) => {
              setDepartamentoId(e.target.value);
              setPage(1);
            }}
          >
            <option value="">{t.people.allDepts}</option>
            {departamentos.map((d) => (
              <option key={d.id} value={d.id}>
                {d.nombre}
              </option>
            ))}
          </select>
          {/* Grid / List toggle */}
          <div
            style={{
              display: 'flex',
              border: '1px solid #D1D5DB',
              borderRadius: 8,
              overflow: 'hidden',
              marginLeft: 'auto',
            }}
          >
            <button
              onClick={() => toggleView('grid')}
              style={{
                padding: '6px 10px',
                border: 'none',
                background: viewMode === 'grid' ? '#0E243E' : '#fff',
                color: viewMode === 'grid' ? '#fff' : C.gray500,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
              }}
              title="Grid"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              >
                <rect x="3" y="3" width="7" height="7" />
                <rect x="14" y="3" width="7" height="7" />
                <rect x="3" y="14" width="7" height="7" />
                <rect x="14" y="14" width="7" height="7" />
              </svg>
            </button>
            <button
              onClick={() => toggleView('list')}
              style={{
                padding: '6px 10px',
                border: 'none',
                borderLeft: '1px solid #D1D5DB',
                background: viewMode === 'list' ? '#0E243E' : '#fff',
                color: viewMode === 'list' ? '#fff' : C.gray500,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
              }}
              title="Lista"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              >
                <line x1="8" y1="6" x2="21" y2="6" />
                <line x1="8" y1="12" x2="21" y2="12" />
                <line x1="8" y1="18" x2="21" y2="18" />
                <line x1="3" y1="6" x2="3.01" y2="6" />
                <line x1="3" y1="12" x2="3.01" y2="12" />
                <line x1="3" y1="18" x2="3.01" y2="18" />
              </svg>
            </button>
          </div>
        </div>

        {/* Bulk actions bar */}
        {selectedIds.size > 0 && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '8px 16px',
              marginBottom: 12,
              background: '#EFF6FF',
              border: '1px solid #BFDBFE',
              borderRadius: 8,
            }}
          >
            <span style={{ fontSize: 13, fontWeight: 600, color: '#1D4ED8' }}>
              {t.people.selected(selectedIds.size)}
            </span>
            <button
              onClick={handleExportCsv}
              style={{
                padding: '5px 12px',
                border: '1px solid #D1D5DB',
                borderRadius: 6,
                background: '#fff',
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: 600,
                fontFamily: 'inherit',
              }}
            >
              {t.people.bulkExportCsv(selectedIds.size)}
            </button>
            <button
              onClick={() => setSelectedIds(new Set())}
              style={{
                marginLeft: 'auto',
                padding: '5px 12px',
                border: 'none',
                borderRadius: 6,
                background: 'transparent',
                cursor: 'pointer',
                fontSize: 12,
                color: C.gray500,
                fontFamily: 'inherit',
              }}
            >
              {t.common.cancelar}
            </button>
          </div>
        )}

        {/* Person Cards / List */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: C.gray400 }}>
            {t.people.loadingDirectory}
          </div>
        ) : personas.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: C.gray400 }}>
            {t.people.noPersonsFound}
          </div>
        ) : viewMode === 'grid' ? (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
              gap: 12,
            }}
          >
            {personas.map((p) => (
              <div key={p.id} style={{ position: 'relative' }}>
                <input
                  type="checkbox"
                  checked={selectedIds.has(p.id)}
                  onChange={() => toggleSelect(p.id)}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    zIndex: 2,
                    width: 16,
                    height: 16,
                    cursor: 'pointer',
                    accentColor: '#1D4ED8',
                  }}
                />
                <PersonaCard persona={p} onClick={() => setDrawerPersonaId(p.id)} />
              </div>
            ))}
          </div>
        ) : (
          <div
            style={{
              background: '#fff',
              borderRadius: 10,
              border: '1px solid #E2E6ED',
              overflow: 'hidden',
            }}
          >
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#0E243E', color: '#fff', textAlign: 'left' }}>
                  <th style={{ padding: '10px 12px', width: 36 }}>
                    <input
                      type="checkbox"
                      checked={personas.length > 0 && selectedIds.size === personas.length}
                      onChange={toggleSelectAll}
                      style={{ width: 15, height: 15, cursor: 'pointer', accentColor: '#33BCE1' }}
                    />
                  </th>
                  <th style={{ padding: '10px 12px', fontWeight: 600 }}></th>
                  <th style={{ padding: '10px 12px', fontWeight: 600 }}>{t.people.fullName}</th>
                  <th style={{ padding: '10px 12px', fontWeight: 600 }}>{t.people.position}</th>
                  <th style={{ padding: '10px 12px', fontWeight: 600 }}>{t.people.department}</th>
                  <th style={{ padding: '10px 12px', fontWeight: 600 }}>{t.people.email}</th>
                  <th style={{ padding: '10px 12px', fontWeight: 600 }}>{t.people.type}</th>
                  <th style={{ padding: '10px 12px', fontWeight: 600 }}>{t.people.status}</th>
                </tr>
              </thead>
              <tbody>
                {personas.map((p) => {
                  const puesto = p.puestoActual?.puesto;
                  const tipoBadge = TIPO_BADGE_COLORS[p.tipoPersona] ?? {
                    bg: '#F3F4F6',
                    text: C.gray500,
                  };
                  const estadoBadge = ESTADO_BADGE_COLORS[p.estado] ?? {
                    bg: '#F3F4F6',
                    text: C.gray500,
                  };
                  return (
                    <tr
                      key={p.id}
                      onClick={() => setDrawerPersonaId(p.id)}
                      style={{
                        cursor: 'pointer',
                        borderBottom: '1px solid #F3F4F6',
                        background: selectedIds.has(p.id) ? '#EFF6FF' : undefined,
                      }}
                      onMouseEnter={(e) => {
                        if (!selectedIds.has(p.id)) e.currentTarget.style.background = '#F8FAFC';
                      }}
                      onMouseLeave={(e) => {
                        if (!selectedIds.has(p.id)) e.currentTarget.style.background = '';
                      }}
                    >
                      <td style={{ padding: '8px 12px' }}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(p.id)}
                          onChange={() => toggleSelect(p.id)}
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            width: 15,
                            height: 15,
                            cursor: 'pointer',
                            accentColor: '#1D4ED8',
                          }}
                        />
                      </td>
                      <td style={{ padding: '8px 12px' }}>
                        <div
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: 8,
                            background: p.fotoUrl
                              ? `url(${p.fotoUrl}) center/cover`
                              : getDeptColor(puesto?.departamento?.nombre),
                            color: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 12,
                            fontWeight: 700,
                          }}
                        >
                          {!p.fotoUrl && getInitials(p.nombre, p.apellidos)}
                        </div>
                      </td>
                      <td style={{ padding: '8px 12px', fontWeight: 600, color: '#1A1D23' }}>
                        {p.nombre} {p.apellidos}
                      </td>
                      <td style={{ padding: '8px 12px', color: C.gray700 }}>
                        {puesto?.titulo ?? '—'}
                      </td>
                      <td style={{ padding: '8px 12px', color: C.gray500 }}>
                        {puesto?.departamento?.nombre ?? '—'}
                      </td>
                      <td style={{ padding: '8px 12px', color: C.gray500 }}>{p.email ?? '—'}</td>
                      <td style={{ padding: '8px 12px' }}>
                        <span
                          style={{
                            padding: '2px 8px',
                            borderRadius: 4,
                            fontSize: 11,
                            fontWeight: 500,
                            background: tipoBadge.bg,
                            color: tipoBadge.text,
                          }}
                        >
                          {p.tipoPersona}
                        </span>
                      </td>
                      <td style={{ padding: '8px 12px' }}>
                        <span
                          style={{
                            padding: '2px 8px',
                            borderRadius: 4,
                            fontSize: 11,
                            fontWeight: 500,
                            background: estadoBadge.bg,
                            color: estadoBadge.text,
                          }}
                        >
                          {p.estado}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalItems > 0 && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: 20,
              flexWrap: 'wrap',
              gap: 8,
            }}
          >
            {/* Showing range */}
            <span style={{ fontSize: 12, color: '#5A6171' }}>
              {t.people.showing(
                (page - 1) * pageSize + 1,
                Math.min(page * pageSize, totalItems),
                totalItems,
              )}
            </span>

            {/* Page controls */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                style={{
                  padding: '6px 12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: 6,
                  background: '#fff',
                  cursor: page <= 1 ? 'default' : 'pointer',
                  opacity: page <= 1 ? 0.5 : 1,
                  fontSize: 12,
                  fontFamily: 'inherit',
                }}
              >
                {t.people.previous}
              </button>
              <span style={{ fontSize: 12, color: '#5A6171' }}>
                {t.people.pageOf(page, totalPages)}
              </span>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                style={{
                  padding: '6px 12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: 6,
                  background: '#fff',
                  cursor: page >= totalPages ? 'default' : 'pointer',
                  opacity: page >= totalPages ? 0.5 : 1,
                  fontSize: 12,
                  fontFamily: 'inherit',
                }}
              >
                {t.people.next}
              </button>
            </div>

            {/* Page size selector */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
                style={{
                  padding: '4px 8px',
                  border: '1px solid #D1D5DB',
                  borderRadius: 6,
                  fontSize: 12,
                  fontFamily: 'inherit',
                  background: '#fff',
                }}
              >
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span style={{ fontSize: 12, color: '#5A6171' }}>{t.people.pageSize}</span>
            </div>
          </div>
        )}
      </div>

      <PersonaDrawer personaId={drawerPersonaId} onClose={() => setDrawerPersonaId(null)} />
    </div>
  );
}
